/**
 * Pestaña Materialidad — hallazgos EFOS / art. 69-B (cruce preventivo).
 * «Limpio» = sin match en lista; no valida CSF/contratos/PPD del expediente.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ExternalLink, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { goToSatVisorForUuid } from "@/lib/satVisorNavigation";
import {
  fetchMaterialidadEfosHallazgos,
  refreshMaterialidadEfosHallazgos,
} from "@/modules/tramites/materialidad/materialidadApi";

const SITUACION_LABEL = {
  presunto: "Presunto",
  definitivo: "Definitivo",
  desvirtuado: "Desvirtuado",
  sentencia_favorable: "Sentencia favorable",
};

function situacionLabel(norm) {
  return SITUACION_LABEL[norm] || norm || "—";
}

function fmtMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

export default function MaterialidadEfosPanel({ clientId, clients = [] }) {
  const navigate = useNavigate();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [report, setReport] = useState(null);
  const [expandedId, setExpandedId] = useState("");

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2].map(String);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMaterialidadEfosHallazgos({
        year: Number(year),
        clientId: clientId || undefined,
      });
      setReport(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el cruce EFOS.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const data = await refreshMaterialidadEfosHallazgos({
        year: Number(year),
        clientId: clientId || undefined,
      });
      setReport(data?.report || null);
      const r = data?.refresh || {};
      if (r.clients_with_findings > 0) {
        toast.warning(
          `Cruce EFOS: ${r.clients_with_findings} empresa(s) con hallazgos · `
          + `${r.total_hallazgos || 0} RFC en lista.`,
        );
      } else {
        toast.success(
          `Cruce EFOS: ${r.clients_audited || 0} empresa(s) revisadas · sin hallazgos 69-B.`,
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo refrescar el cruce EFOS.");
    } finally {
      setRefreshing(false);
    }
  };

  const openVisor = async (cid, uuid) => {
    try {
      await goToSatVisorForUuid(navigate, cid, uuid);
    } catch {
      toast.error("No se pudo abrir el CFDI en el Visor.");
    }
  };

  const findings = report?.findings_only || [];
  const limpios = (report?.snapshots || []).filter((s) => Number(s.hallazgos || 0) === 0);
  const listaAt = report?.lista?.updated_at
    ? new Date(report.lista.updated_at).toLocaleString("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
    })
    : null;

  return (
    <div className="space-y-4" data-testid="materialidad-efos-panel">
      <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5 shadow-sm space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500" htmlFor="materialidad-efos-year">Año CFDI</label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger id="materialidad-efos-year" className="rounded-sm w-[100px] h-8" data-testid="materialidad-efos-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[160px] text-[11px] text-slate-600">
            {listaAt
              ? `Lista 69-B local: ${listaAt}`
              : "Sin lista 69-B cargada — actualice en Datos Maestros → Auditoría EFOS."}
            <span className="block text-[10px] text-slate-500 mt-0.5">
              El job automático guarda este cruce al cambiar la lista. «Limpio» ≠ expediente documental validado.
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm h-8"
            disabled={loading}
            onClick={load}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Leer
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm h-8"
            disabled={loading || refreshing}
            onClick={handleRefresh}
            data-testid="materialidad-efos-refresh"
            title="Cruza RFC de CFDI vs lista 69-B; no ejecuta Analizar riesgos CFDI"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldAlert className="h-4 w-4 mr-1" />}
            Actualizar cruce EFOS
          </Button>
          <Button type="button" variant="ghost" size="sm" className="rounded-sm h-8 text-xs" asChild>
            <a href="/auditoria-efos">
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              Auditoría EFOS
            </a>
          </Button>
        </div>
      </div>

      {report?.disclaimer ? (
        <p className="text-[11px] text-slate-600 m-0 rounded-sm border border-amber-100 bg-amber-50/80 px-3 py-2" role="note">
          {report.disclaimer}
        </p>
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="materialidad-efos-kpis">
        <div className="rounded-sm border border-slate-200 bg-white px-3 py-2">
          <p className="text-[10px] text-slate-500 m-0">Empresas cruzadas</p>
          <p className="text-lg font-semibold text-[#0F2D5C] m-0">{report?.clients_audited ?? "—"}</p>
        </div>
        <div className="rounded-sm border border-rose-200 bg-rose-50/50 px-3 py-2">
          <p className="text-[10px] text-rose-800 m-0">Con hallazgos</p>
          <p className="text-lg font-semibold text-rose-900 m-0">{report?.clients_with_findings ?? "—"}</p>
        </div>
        <div className="rounded-sm border border-emerald-200 bg-emerald-50/40 px-3 py-2">
          <p className="text-[10px] text-emerald-800 m-0">EFOS limpio</p>
          <p className="text-lg font-semibold text-emerald-900 m-0">{report?.clients_limpios ?? "—"}</p>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white px-3 py-2">
          <p className="text-[10px] text-slate-500 m-0">CFDI en lista</p>
          <p className="text-lg font-semibold text-[#0F2D5C] m-0">{report?.cfdi_en_lista ?? "—"}</p>
        </div>
      </div>

      {loading && !report ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando cruce EFOS…
        </div>
      ) : null}

      {!loading && findings.length === 0 ? (
        <div
          className="rounded-sm border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-600"
          data-testid="materialidad-efos-empty"
        >
          {report?.clients_audited
            ? "Sin hallazgos 69-B en el año seleccionado (EFOS limpio en empresas cruzadas)."
            : "Aún no hay cruces guardados. Pulse «Actualizar cruce EFOS» o espere la verificación automática de la lista."}
        </div>
      ) : null}

      {findings.length > 0 ? (
        <div className="space-y-2" data-testid="materialidad-efos-findings">
          <h3 className="text-sm font-semibold text-[#0F2D5C] m-0 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 text-rose-700" />
            Empresas con contrapartes en lista 69-B
          </h3>
          {findings.map((snap) => {
            const open = expandedId === snap.client_id;
            return (
              <div
                key={snap.client_id}
                className="rounded-sm border border-rose-200 bg-white overflow-hidden"
                data-testid={`materialidad-efos-client-${snap.client_id}`}
              >
                <button
                  type="button"
                  className="w-full text-left px-3 py-2.5 flex flex-wrap items-center gap-2 hover:bg-rose-50/40"
                  onClick={() => setExpandedId(open ? "" : snap.client_id)}
                >
                  <span className="font-medium text-sm text-[#0F2D5C]">
                    {snap.client_razon_social || snap.client_id}
                  </span>
                  {snap.client_rfc ? (
                    <span className="font-mono text-[11px] text-slate-600">{snap.client_rfc}</span>
                  ) : null}
                  <span className="ml-auto text-[11px] text-rose-800 font-semibold">
                    {snap.hallazgos} RFC · {snap.cfdi_en_lista} CFDI
                  </span>
                </button>
                {open ? (
                  <div className="border-t border-rose-100 px-3 py-2 space-y-3 bg-rose-50/20">
                    {(snap.findings || []).map((f) => (
                      <div key={f.rfc} className="rounded-sm border border-slate-200 bg-white p-2.5">
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                          <span className="font-semibold text-slate-900">{f.nombre_lista || f.rfc}</span>
                          <span className="font-mono text-slate-600">{f.rfc}</span>
                          <span className="rounded-sm border border-rose-200 bg-rose-50 px-1.5 text-rose-900">
                            {situacionLabel(f.situacion_norm)}
                          </span>
                          {(f.roles || []).length ? (
                            <span className="text-slate-500">{(f.roles || []).join(" · ")}</span>
                          ) : null}
                        </div>
                        <ul className="mt-2 space-y-1 m-0 pl-0 list-none">
                          {(f.cfdis || []).map((c) => (
                            <li
                              key={c.uuid || `${f.rfc}-${c.fecha}`}
                              className="flex flex-wrap items-center gap-2 text-[11px] text-slate-700"
                            >
                              <span className="font-mono break-all">{c.uuid || "—"}</span>
                              <span>{(c.fecha || "").slice(0, 10)}</span>
                              <span>{c.tipo_comprobante || "—"}</span>
                              <span>{fmtMoney(c.total)}</span>
                              {c.uuid ? (
                                <button
                                  type="button"
                                  className="text-[#0F2D5C] underline underline-offset-2"
                                  onClick={() => openVisor(snap.client_id, c.uuid)}
                                >
                                  Ver en Visor
                                </button>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {limpios.length > 0 && !clientId ? (
        <details className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-700">
            Empresas con EFOS limpio ({limpios.length})
          </summary>
          <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
            {limpios.map((s) => (
              <li key={s.client_id}>
                {s.client_razon_social || s.client_id}
                {s.client_rfc ? ` · ${s.client_rfc}` : ""}
                <span className="text-slate-400"> · {s.cfdi_revisados || 0} CFDI</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
